import {
  browserSupportsWebAuthnAutofill,
  startAuthentication,
  startRegistration,
  type AuthenticationResponseJSON,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
  type RegistrationResponseJSON,
} from '@simplewebauthn/browser';

export { browserSupportsWebAuthnAutofill };

export type DismissDuration = 'short' | 'long';

/**
 * PasskeyDismissedError signals that the user closed the WebAuthn prompt —
 * by hitting cancel, the browser's timeout, or otherwise choosing not to
 * proceed. Callers should treat it as a no-op (no banner, no redirect) and
 * leave the form alone.
 */
export class PasskeyDismissedError extends Error {
  constructor() {
    super('Passkey prompt dismissed');
    this.name = 'PasskeyDismissedError';
    // tsconfig targets ES5. Extending built-ins like Error under ES5 emit
    // leaves the prototype chain broken, so `err instanceof
    // PasskeyDismissedError` comes back false at the call site. Restore
    // the prototype explicitly per the TS handbook's guidance on this.
    Object.setPrototypeOf(this, PasskeyDismissedError.prototype);
  }
}

/**
 * isPasskeyDismissal recognises "the user walked away" cases coming out of
 * navigator.credentials.get():
 *   - DOMException NotAllowedError — ESC / close / per-spec privacy guard
 *     (all browsers funnel user-cancel through this; the wording is the
 *     infamous "The operation either timed out or was not allowed" text).
 *   - DOMException AbortError — the authentication ceremony was aborted
 *     (e.g. a competing call or the abort signal fired).
 *   - simplewebauthn wraps the above in WebAuthnError. Its constructor
 *     copies cause.name onto itself, but older cached bundles / transpiles
 *     have dropped the override, so we inspect `.cause` as a fallback.
 *   - Some browsers resolve navigator.credentials.get() with a null
 *     credential instead of rejecting on dismiss; simplewebauthn then
 *     throws a plain Error with this exact message. Match it by text —
 *     it's the only stable hook we have.
 */
function isPasskeyDismissal(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  if (err.name === 'NotAllowedError' || err.name === 'AbortError') return true;
  const { cause } = err as { cause?: unknown };
  if (cause instanceof Error && (cause.name === 'NotAllowedError' || cause.name === 'AbortError')) {
    return true;
  }
  if (err.message === 'Authentication was not completed') return true;
  return false;
}

/**
 * browserSupportsPasskeys is a feature-detect for the WebAuthn API. Older
 * browsers (including Safari < 14, IE) do not expose navigator.credentials
 * and the Passkey button should not appear there — the user will fall back
 * to password / Google. The compat lint rule can't tell that these are
 * guards exactly for that reason, so suppress it here.
 */
export function browserSupportsPasskeys(): boolean {
  return typeof window !== 'undefined'
    // eslint-disable-next-line compat/compat
    && typeof window.PublicKeyCredential !== 'undefined'
    && typeof navigator !== 'undefined'
    // eslint-disable-next-line compat/compat
    && typeof navigator.credentials?.get === 'function';
}

async function extractError(res: Response): Promise<string> {
  try {
    const body = await res.clone().json();
    if (body?.message) return body.message;
  } catch {
    // non-JSON body — fall through to status line
  }
  return `${res.status} ${res.statusText}`;
}

// Accept: application/json gets 200 + redirect_url instead of a 302 that fetch
// would follow cross-origin into app.* and fail on CORS.
async function finishPasskeyLogin(assertion: AuthenticationResponseJSON): Promise<string> {
  const res = await fetch('/api/passkey/login/finish', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(assertion),
  });
  if (res.status >= 400) {
    throw new Error(await extractError(res));
  }
  const body = await res.json() as { redirect_url?: unknown };
  if (typeof body?.redirect_url !== 'string') {
    throw new Error('Passkey login returned no redirect');
  }
  return body.redirect_url;
}

/**
 * loginWithPasskeyDiscoverable runs the discoverable ("resident key")
 * WebAuthn login ceremony triggered by an explicit button click — the
 * Mercury-style "Sign In With Passkey" affordance. No email is collected
 * up front: we POST /api/passkey/login/begin WITHOUT an email, the backend
 * answers with an empty-allowCredentials challenge, and the browser opens
 * its own modal account picker listing every passkey enrolled for this
 * origin. The backend resolves the user from the credential ID returned in
 * the assertion on Finish.
 *
 * Unlike loginWithPasskeyConditional this uses the default modal mediation
 * (no useBrowserAutofill) so it works even where conditional autofill is
 * unavailable, and it maps user dismissal to PasskeyDismissedError so the
 * caller can stay silent instead of showing an error.
 */
export async function loginWithPasskeyDiscoverable(): Promise<string> {
  const beginRes = await fetch('/api/passkey/login/begin', {
    method: 'POST',
    credentials: 'include',
  });
  if (!beginRes.ok) {
    throw new Error(await extractError(beginRes));
  }
  const { publicKey } = await beginRes.json() as { publicKey: PublicKeyCredentialRequestOptionsJSON };

  let assertion: AuthenticationResponseJSON;
  try {
    assertion = await startAuthentication({ optionsJSON: publicKey });
  } catch (err) {
    if (isPasskeyDismissal(err)) {
      throw new PasskeyDismissedError();
    }
    throw new Error(err instanceof Error ? err.message : 'Passkey authentication failed');
  }

  return finishPasskeyLogin(assertion);
}

/**
 * loginWithPasskeyConditional arms the WebAuthn conditional-mediation
 * ("autofill") flow: the browser silently holds the passkey prompt and
 * surfaces it in the username field's autofill dropdown when the user
 * focuses it. The promise resolves only when the user actually picks a
 * passkey from that dropdown; otherwise it never resolves (browser times
 * out or the user submits the form another way — we don't care).
 *
 * We call the same POST /api/passkey/login/begin endpoint but WITHOUT an
 * email; the backend answers with a discoverable-flow challenge (empty
 * allowCredentials). On Finish the backend resolves the user from the
 * credential ID that the browser returns in the assertion.
 *
 * Caller should only invoke this when browserSupportsWebAuthnAutofill()
 * resolves to true. The DOM must already contain an input with
 * `autocomplete="username webauthn"` — simplewebauthn throws synchronously
 * otherwise.
 */
export async function loginWithPasskeyConditional(): Promise<string> {
  const beginRes = await fetch('/api/passkey/login/begin', {
    method: 'POST',
    credentials: 'include',
  });
  if (!beginRes.ok) {
    throw new Error(await extractError(beginRes));
  }
  const { publicKey } = await beginRes.json() as { publicKey: PublicKeyCredentialRequestOptionsJSON };

  const assertion = await startAuthentication({
    optionsJSON: publicKey,
    useBrowserAutofill: true,
  });

  return finishPasskeyLogin(assertion);
}

/**
 * registerPasskey runs the full WebAuthn enrollment ceremony against an
 * already-authenticated session (the backend's /api/passkey/register/begin
 * requires session.IsLoggedIn()). Used by the post-login prompt view.
 */
export async function registerPasskey(): Promise<void> {
  const beginRes = await fetch('/api/passkey/register/begin', {
    method: 'POST',
    credentials: 'include',
  });
  if (!beginRes.ok) {
    throw new Error(await extractError(beginRes));
  }
  const { publicKey } = await beginRes.json() as { publicKey: PublicKeyCredentialCreationOptionsJSON };

  let attestation: RegistrationResponseJSON;
  try {
    attestation = await startRegistration({ optionsJSON: publicKey });
  } catch (err) {
    throw new Error(err instanceof Error ? err.message : 'Passkey registration cancelled');
  }

  const finishRes = await fetch('/api/passkey/register/finish', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(attestation),
  });
  if (!finishRes.ok) {
    throw new Error(await extractError(finishRes));
  }
}

/**
 * dismissPasskeyPrompt tells the SSO backend to set the
 * PW-PASSKEY-PROMPT-DISMISSED cookie so the post-login interstitial
 * doesn't fire again for a while. `short` = Skip for now (7d),
 * `long` = Don't ask again (365d).
 */
export async function dismissPasskeyPrompt(duration: DismissDuration): Promise<void> {
  const body = new URLSearchParams();
  body.append('duration', duration);
  const res = await fetch('/api/passkey/dismiss-prompt', {
    method: 'POST',
    credentials: 'include',
    body,
  });
  if (!res.ok) {
    throw new Error(await extractError(res));
  }
}
